/**
 * 로그인 연동 현황 — 슈퍼관리자 (#219)
 *
 * 서비스마다 콘솔 로그인(OIDC)이 붙었는지, 끝까지 되는지, 어디서 막히는지를 한 표로 본다.
 * 새 프로젝트를 붙이는 일은 그 서비스 줄이 「정상」이 되면 끝난다.
 * 상태는 백엔드가 판정한다. 이 화면은 받은 값을 그리기만 한다.
 */
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { oidcIntegrationAPI, Integration, IntegrationStatus } from '@/services/apis/serviceClientAPI';
import {
  INTEGRATION_FAILURE_REASONS,
  INTEGRATION_PERIODS,
  INTEGRATION_STATUS,
  INTEGRATION_STATUS_ORDER,
  INTEGRATION_STEP_LABELS,
} from '@/constants/oidcIntegration';
import { formatDateTimeToKST } from '@/utils/dateUtils';
import { getErrorMessage } from '@/utils/errorUtils';

const COLUMN_COUNT = 7;

const when = (value: string | null): string => (value ? formatDateTimeToKST(value) : '-');

const StatusBadge: React.FC<{ status: IntegrationStatus }> = ({ status }) => {
  const info = INTEGRATION_STATUS[status];
  return <Badge variant={info.variant} className="whitespace-nowrap">{info.label}</Badge>;
};

const StatusSummary: React.FC<{ rows: Integration[] }> = ({ rows }) => (
  <div className="flex flex-wrap gap-2">
    {INTEGRATION_STATUS_ORDER.map((status) => {
      const count = rows.filter((row) => row.status === status).length;
      return (
        <div key={status} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <StatusBadge status={status} />
          <span className="font-semibold">{count}</span>
        </div>
      );
    })}
  </div>
);

const IntegrationDetail: React.FC<{ row: Integration }> = ({ row }) => (
  <div className="space-y-4 bg-gray-50 p-4 text-sm">
    <p className="text-gray-700">{INTEGRATION_STATUS[row.status].hint}</p>

    {row.client && (
      <div className="grid gap-1 md:grid-cols-[8rem_1fr]">
        <span className="text-gray-500">client_id</span>
        <code className="break-all">{row.client.client_id}</code>
        <span className="text-gray-500">콜백 주소</span>
        <div>
          {row.client.redirect_uris.map((uri) => (
            <code key={uri} className="block break-all">{uri}</code>
          ))}
        </div>
        <span className="text-gray-500">발급일</span>
        <span>{when(row.client.created_at)}</span>
      </div>
    )}

    {row.stats.unfinished > 0 && (
      <p className="text-amber-700">
        콘솔에서 코드를 받은 뒤 토큰 교환까지 가지 않은 로그인이 {row.stats.unfinished}건 있습니다.
        서비스의 콜백 처리를 확인해 주세요.
      </p>
    )}

    {row.stats.failure_reasons.length > 0 && (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>단계</TableHead>
            <TableHead>실패 사유</TableHead>
            <TableHead className="text-right">건수</TableHead>
            <TableHead>마지막 발생</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {row.stats.failure_reasons.map((item) => (
            <TableRow key={`${item.step}-${item.reason}`}>
              <TableCell>{INTEGRATION_STEP_LABELS[item.step] ?? item.step}</TableCell>
              <TableCell>
                <div>{INTEGRATION_FAILURE_REASONS[item.reason] ?? item.reason}</div>
                <code className="text-xs text-gray-500">{item.reason}</code>
              </TableCell>
              <TableCell className="text-right">{item.count}</TableCell>
              <TableCell>{when(item.last_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </div>
);

const IntegrationRow: React.FC<{ row: Integration; open: boolean; onToggle: () => void }> = ({
  row, open, onToggle,
}) => (
  <Fragment>
    <TableRow className="cursor-pointer" onClick={onToggle}>
      <TableCell>
        <div className="flex items-center gap-2 font-medium">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {row.service_name}
        </div>
        <div className="pl-6 text-xs text-gray-500">{row.service_slug}</div>
      </TableCell>
      <TableCell><StatusBadge status={row.status} /></TableCell>
      <TableCell className="whitespace-nowrap">{when(row.last_login_at)}</TableCell>
      <TableCell className="text-right">{row.stats.token_success}</TableCell>
      <TableCell className="text-right">{row.stats.unfinished}</TableCell>
      <TableCell className="text-right">{row.stats.failures}</TableCell>
      <TableCell className="whitespace-nowrap">{when(row.stats.last_failure_at)}</TableCell>
    </TableRow>
    {open && (
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} className="p-0">
          <IntegrationDetail row={row} />
        </TableCell>
      </TableRow>
    )}
  </Fragment>
);

const useIntegrations = (days: number) => {
  const [rows, setRows] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await oidcIntegrationAPI.list(days);
      setRows(result.integrations);
    } catch (e) {
      setError(getErrorMessage(e, '로그인 연동 현황을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  return { rows, loading, error, setError, load };
};

const LoginIntegrationPage: React.FC = () => {
  const [days, setDays] = useState<number>(7);
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const { rows, loading, error, setError, load } = useIntegrations(days);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">로그인 연동 현황</h2>
        <p className="mt-1 text-sm text-gray-600">
          서비스별 콘솔 로그인 연동 상태입니다. 줄을 선택하면 실패 사유와 클라이언트 정보가 표시됩니다.
        </p>
      </div>

      {error && <AlertMessage type="error" message={error} onClose={() => setError('')} />}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>서비스 목록 <span className="ml-2 text-sm text-gray-500">({rows.length}개)</span></CardTitle>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(value) => setDays(Number(value))}>
              <SelectTrigger className="h-9 w-32" aria-label="집계 기간">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTEGRATION_PERIODS.map((period) => (
                  <SelectItem key={period} value={String(period)}>최근 {period}일</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? '불러오는 중' : '새로고침'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && rows.length === 0 ? (
            <LoadingSpinner message="로그인 연동 현황을 불러오는 중입니다." />
          ) : rows.length === 0 ? (
            <EmptyState message="등록된 서비스가 없습니다. 서비스 관리에서 서비스를 등록하면 여기에 표시됩니다." />
          ) : (
            <div className={loading ? 'opacity-60' : ''}>
              <StatusSummary rows={rows} />
              <div className="mt-4">
                {/* 좁은 화면에서는 글자 단위로 꺾이지 않고 가로로 넘긴다(공통 Table 이 스크롤을 감싼다) */}
                <Table className="min-w-[760px]">
                  <TableHeader>
                    <TableRow className="whitespace-nowrap">
                      <TableHead>서비스</TableHead>
                      <TableHead>상태</TableHead>
                      <TableHead>마지막 로그인</TableHead>
                      <TableHead className="text-right">로그인 성공</TableHead>
                      <TableHead className="text-right">미완료</TableHead>
                      <TableHead className="text-right">실패</TableHead>
                      <TableHead>마지막 실패</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <IntegrationRow
                        key={row.service_slug}
                        row={row}
                        open={openSlug === row.service_slug}
                        onToggle={() => setOpenSlug(openSlug === row.service_slug ? null : row.service_slug)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginIntegrationPage;
